import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiAlertCircle,
  FiCheck,
  FiSearch,
  FiShoppingCart,
} from "react-icons/fi";
import {
  useGetPOSAvailableStockQuery,
  useGetProductsQuery,
} from "../services/api";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", {
    style: "currency",
    currency: "ETB",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function normalizeText(value) {
  return String(value || "").toLowerCase().trim();
}

function getCartLineTotal(item) {
  return Number(item.unit_price || 0) * Number(item.custom_quantity || 0);
}

export default function POSPage_Premium() {
  const navigate = useNavigate();
  const { data: productsData, isLoading: isProductsLoading } = useGetProductsQuery({ active: true });
  const { data: stockData } = useGetPOSAvailableStockQuery();

  const products = useMemo(() => productsData?.data || [], [productsData]);
  const stockItems = useMemo(() => stockData?.data || [], [stockData]);

  // Load cart from localStorage on mount
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("pos_cart");
    return saved ? JSON.parse(saved) : [];
  });
  
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("pos_cart", JSON.stringify(cart));
  }, [cart]);

  const categories = useMemo(() => {
    return [...new Set(products.map((product) => product.category).filter(Boolean))].sort();
  }, [products]);

  const stockByMaterial = useMemo(() => {
    return stockItems.reduce((summary, stock) => {
      const material = stock.material_type;
      if (!material) return summary;
      const current = summary[material] || { quantity: 0, unit: stock.unit };
      return {
        ...summary,
        [material]: {
          unit: current.unit || stock.unit,
          quantity: current.quantity + Number(stock.available_quantity || 0),
        },
      };
    }, {});
  }, [stockItems]);

  const filteredProducts = useMemo(() => {
    const term = normalizeText(searchTerm);
    return products.filter((product) => {
      const searchable = [
        product.name,
        product.sku,
        product.material_type,
        product.category,
        product.description,
      ]
        .map(normalizeText)
        .join(" ");

      const matchesSearch = !term || searchable.includes(term);
      const matchesCategory = !categoryFilter || product.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, categoryFilter]);

  const cartTotal = cart.reduce((sum, item) => sum + getCartLineTotal(item), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  const addToCart = (product) => {
    setError("");
    setSuccess("");
    
    const availableStock = stockItems.find(
      (stock) => stock.material_type === product.material_type && Number(stock.available_quantity) > 0
    );

    if (!availableStock) {
      setError(`No available stock for ${product.material_type || "this product"}.`);
      setTimeout(() => setError(""), 3000);
      return;
    }

    const isGramBased = product.unit === 'grams';
    const defaultWeight = isGramBased ? 100 : Number(product.weight_grams || 1);
    const maxAvailable = isGramBased ? Number(product.weight_grams) : Number(availableStock.available_quantity);

    setCart((currentCart) => {
      const existingIndex = currentCart.findIndex(
        (item) => item.product_id === product.id && item.stock_item_id === availableStock.id
      );

      if (existingIndex >= 0) {
        return currentCart.map((item, index) => {
          if (index !== existingIndex) return item;
          const nextQuantity = Number(item.quantity || 0) + 1;
          const nextCustomQuantity = isGramBased ? Number(item.custom_quantity || 0) + 100 : Number(item.custom_quantity || 0) + defaultWeight;
          return {
            ...item,
            quantity: nextQuantity,
            pieces_count: nextQuantity,
            custom_quantity: Number(nextCustomQuantity.toFixed(2)),
          };
        });
      }

      setSuccess(`✓ ${product.name} added to cart!`);
      setTimeout(() => setSuccess(""), 2000);

      return [
        ...currentCart,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: product.selling_price,
          stock_item_id: availableStock.id,
          stock_item_name: availableStock.name,
          custom_quantity: defaultWeight,
          custom_unit: isGramBased ? 'grams' : availableStock.unit,
          pieces_count: 1,
          storage_note: "",
          weight_grams: product.weight_grams,
          material_type: product.material_type,
          origin: availableStock.origin,
          available_quantity: maxAvailable,
          product_unit: product.unit || 'pc',
          sku: product.sku,
        },
      ];
    });
  };

  const removeFromCart = (index) => {
    setCart((currentCart) => currentCart.filter((_, i) => i !== index));
  };

  const updateCartItem = (index, field, value) => {
    setCart((currentCart) =>
      currentCart.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  return (
    <div style={{
      background: "#F8F9FA",
      minHeight: "100vh",
      padding: "2rem",
      display: "flex",
      gap: "1.5rem",
    }}>
      {/* Main Content Area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "1.5rem", minWidth: 0 }}>
        {/* Page Header with KPIs */}
        <div>
          <div style={{ marginBottom: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <div style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#10B981"
              }} />
              <span style={{ fontSize: "0.8rem", color: "#64748B", fontWeight: 500 }}>
                {new Date().toLocaleDateString("en-ET", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </span>
            </div>
            <h1 style={{ 
              fontSize: "2rem", 
              marginBottom: "0.5rem", 
              fontWeight: 600,
              color: "#1E293B"
            }}>
              Point of Sale
            </h1>
            <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
              Quick and easy sales for your jewellery business
            </p>
          </div>

          {/* KPI Cards Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            {/* Today's Sales */}
            <div style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.25rem",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.125rem"
                }}>
                  🛍️
                </div>
                <div style={{
                  fontSize: "0.75rem",
                  color: "#10B981",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem"
                }}>
                  ↑ 20%
                </div>
              </div>
              <div style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: "0.35rem", fontWeight: 500 }}>
                Today's Sales
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1E293B" }}>
                ETB 80,000.00
              </div>
            </div>

            {/* Total Orders */}
            <div style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.25rem",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.125rem"
                }}>
                  🛒
                </div>
                <div style={{
                  fontSize: "0.75rem",
                  color: "#10B981",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem"
                }}>
                  ↑ 20%
                </div>
              </div>
              <div style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: "0.35rem", fontWeight: 500 }}>
                Total Orders
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1E293B" }}>
                15
              </div>
            </div>

            {/* Products in Stock */}
            <div style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.25rem",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.125rem"
                }}>
                  📦
                </div>
                <div style={{
                  fontSize: "0.75rem",
                  color: "#EF4444",
                  fontWeight: 600
                }}>
                  2 low stock
                </div>
              </div>
              <div style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: "0.35rem", fontWeight: 500 }}>
                Products in Stock
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1E293B" }}>
                1,248
              </div>
            </div>

            {/* Customers */}
            <div style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.25rem",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.125rem"
                }}>
                  👥
                </div>
                <div style={{
                  fontSize: "0.75rem",
                  color: "#10B981",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem"
                }}>
                  ↑ 33%
                </div>
              </div>
              <div style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: "0.35rem", fontWeight: 500 }}>
                Customers
              </div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1E293B" }}>
                8
              </div>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div style={{
            padding: "0.75rem 1rem",
            background: "#D1FAE5",
            border: "1px solid #10B981",
            borderRadius: "8px",
            color: "#047857",
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}>
            <FiCheck size={16} />
            {success}
          </div>
        )}
        {error && (
          <div style={{
            padding: "0.75rem 1rem",
            background: "#FEE2E2",
            border: "1px solid #EF4444",
            borderRadius: "8px",
            color: "#DC2626",
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}>
            <FiAlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Products Section */}
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}>
          {/* Search and Filters */}
          <div style={{ padding: "1.25rem", borderBottom: "1px solid #F1F5F9" }}>
            <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem" }}>
              <div style={{ flex: 1, position: "relative" }}>
                <FiSearch size={16} style={{
                  position: "absolute",
                  left: "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94A3B8",
                }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search products by name, SKU, or barcode..."
                  style={{
                    width: "100%",
                    height: "42px",
                    padding: "0 1rem 0 2.75rem",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    fontSize: "0.9rem",
                    color: "#1E293B",
                    outline: "none",
                    transition: "all 0.2s"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3B82F6";
                    e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#E2E8F0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  minWidth: "180px",
                  height: "42px",
                  padding: "0 1rem",
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  color: "#1E293B",
                  outline: "none",
                  cursor: "pointer"
                }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Category Chips */}
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                onClick={() => setCategoryFilter("")}
                style={{
                  padding: "0.4rem 1rem",
                  border: `2px solid ${!categoryFilter ? "#3B82F6" : "#E2E8F0"}`,
                  background: !categoryFilter ? "#DBEAFE" : "white",
                  borderRadius: "8px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  color: !categoryFilter ? "#1E40AF" : "#64748B",
                  transition: "all 0.2s"
                }}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  style={{
                    padding: "0.4rem 1rem",
                    border: `2px solid ${categoryFilter === cat ? "#3B82F6" : "#E2E8F0"}`,
                    background: categoryFilter === cat ? "#DBEAFE" : "white",
                    borderRadius: "8px",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    color: categoryFilter === cat ? "#1E40AF" : "#64748B",
                    transition: "all 0.2s"
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Product Grid */}
          <div style={{
            flex: 1,
            overflowY: "auto",
            padding: "1.25rem",
            background: "#F8FAFC",
          }}>
            {isProductsLoading ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "3rem" }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #E2E8F0",
                  borderTopColor: "#3B82F6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }} />
                <p style={{ marginTop: "1rem", color: "#64748B" }}>Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "#64748B" }}>
                <FiSearch size={48} style={{ marginBottom: "1rem", opacity: 0.5 }} />
                <p style={{ fontWeight: 600, marginBottom: "0.5rem", color: "#1E293B" }}>No products found</p>
                <p style={{ fontSize: "0.875rem" }}>Try adjusting your search or filters</p>
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: "1rem",
              }}>
                {filteredProducts.map((product) => {
                  const materialStock = stockByMaterial[product.material_type];
                  const availableQuantity = Number(materialStock?.quantity || 0);
                  const isAvailable = availableQuantity > 0;

                  return (
                    <button
                      key={product.id}
                      onClick={() => isAvailable && addToCart(product)}
                      disabled={!isAvailable}
                      style={{
                        background: "white",
                        border: "2px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "1rem",
                        cursor: isAvailable ? "pointer" : "not-allowed",
                        opacity: isAvailable ? 1 : 0.5,
                        textAlign: "left",
                        transition: "all 0.2s",
                        position: "relative",
                      }}
                      onMouseEnter={(e) => {
                        if (isAvailable) {
                          e.currentTarget.style.borderColor = "#3B82F6";
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.2)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#E2E8F0";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      {/* Stock Badge */}
                      <div style={{
                        position: "absolute",
                        top: "0.5rem",
                        right: "0.5rem",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "6px",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        background: isAvailable ? "#D1FAE5" : "#FEE2E2",
                        color: isAvailable ? "#065F46" : "#DC2626",
                      }}>
                        {isAvailable ? `In Stock ${Math.floor(availableQuantity)}` : "Out of Stock"}
                      </div>

                      {/* Product Image */}
                      <div style={{
                        height: "100px",
                        background: "linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "0.75rem",
                        fontSize: "2rem",
                      }}>
                        💍
                      </div>

                      {/* Product Info */}
                      <div>
                        <div style={{
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          marginBottom: "0.25rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#1E293B",
                        }}>
                          {product.name}
                        </div>
                        <div style={{ 
                          fontSize: "0.7rem", 
                          color: "#94A3B8",
                          marginBottom: "0.5rem",
                        }}>
                          SKU: {product.sku || "N/A"}
                        </div>
                        <div style={{
                          fontSize: "1rem",
                          fontWeight: 700,
                          color: "#3B82F6",
                          marginBottom: "0.5rem",
                        }}>
                          {formatCurrency(product.selling_price)}
                        </div>
                        <button
                          style={{
                            width: "100%",
                            padding: "0.5rem",
                            background: isAvailable ? "#3B82F6" : "#E5E7EB",
                            color: isAvailable ? "white" : "#9CA3AF",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            cursor: isAvailable ? "pointer" : "not-allowed",
                          }}
                        >
                          + Add
                        </button>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Sidebar - Current Sale Cart */}
      <div style={{
        width: "380px",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}>
        <div style={{
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 4rem)",
        }}>
          {/* Cart Header */}
          <div style={{ 
            padding: "1.25rem",
            borderBottom: "1px solid #E2E8F0",
            background: "#F8FAFC"
          }}>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", margin: "0 0 0.5rem 0" }}>
              Current Sale
            </h3>
            <div style={{ fontSize: "0.85rem", color: "#64748B" }}>
              {cart.length > 0 ? `${cart.length} item${cart.length > 1 ? "s" : ""} in cart` : "Cart is empty"}
            </div>
          </div>

          {/* Cart Items - Scrollable */}
          <div style={{
            flex: 1,
            overflowY: "auto",
            padding: "1rem",
          }}>
            {cart.length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem 1rem", color: "#94A3B8" }}>
                <FiShoppingCart size={48} style={{ marginBottom: "0.75rem", opacity: 0.5 }} />
                <p style={{ fontWeight: 600, marginBottom: "0.5rem", color: "#64748B" }}>Cart is empty</p>
                <p style={{ fontSize: "0.85rem" }}>Add products to start a sale</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {cart.map((item, index) => (
                  <div key={index} style={{
                    background: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    padding: "0.875rem",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "#1E293B", marginBottom: "0.25rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.product_name}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#64748B" }}>
                          {formatCurrency(item.unit_price)} × {item.quantity}
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#3B82F6", whiteSpace: "nowrap", marginLeft: "0.75rem" }}>
                        {formatCurrency(getCartLineTotal(item))}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <button
                        onClick={() => {
                          if (item.quantity > 1) {
                            updateCartItem(index, "quantity", item.quantity - 1);
                            updateCartItem(index, "custom_quantity", Number((item.custom_quantity - (item.weight_grams || 1)).toFixed(2)));
                          } else {
                            removeFromCart(index);
                          }
                        }}
                        style={{
                          width: "28px",
                          height: "28px",
                          border: "1px solid #E2E8F0",
                          background: "white",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "1rem",
                          fontWeight: 600,
                          color: "#64748B",
                        }}
                      >
                        −
                      </button>
                      <div style={{
                        flex: 1,
                        textAlign: "center",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#1E293B",
                      }}>
                        {item.quantity}
                      </div>
                      <button
                        onClick={() => {
                          updateCartItem(index, "quantity", item.quantity + 1);
                          updateCartItem(index, "custom_quantity", Number((item.custom_quantity + (item.weight_grams || 1)).toFixed(2)));
                        }}
                        style={{
                          width: "28px",
                          height: "28px",
                          border: "1px solid #E2E8F0",
                          background: "white",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "1rem",
                          fontWeight: 600,
                          color: "#64748B",
                        }}
                      >
                        +
                      </button>
                      <button
                        onClick={() => removeFromCart(index)}
                        style={{
                          width: "28px",
                          height: "28px",
                          border: "1px solid #FCA5A5",
                          background: "#FEE2E2",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          color: "#DC2626",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart Footer - Summary & Actions */}
          {cart.length > 0 && (
            <div style={{
              padding: "1.25rem",
              borderTop: "2px solid #E2E8F0",
              background: "#F8FAFC",
            }}>
              {/* Totals */}
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span style={{ fontSize: "0.9rem", color: "#64748B" }}>Subtotal</span>
                  <span style={{ fontSize: "0.9rem", fontWeight: 600, color: "#1E293B" }}>{formatCurrency(cartTotal)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "0.75rem", borderTop: "1px solid #E2E8F0" }}>
                  <span style={{ fontSize: "1rem", fontWeight: 600, color: "#1E293B" }}>Total</span>
                  <span style={{ fontSize: "1.25rem", fontWeight: 700, color: "#3B82F6" }}>{formatCurrency(cartTotal)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <button
                  onClick={() => navigate("/pos/checkout")}
                  style={{
                    width: "100%",
                    padding: "0.875rem",
                    background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(59, 130, 246, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
                  }}
                >
                  Complete Sale
                </button>
                <button
                  onClick={() => {
                    setCart([]);
                    localStorage.removeItem("pos_cart");
                  }}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    background: "white",
                    color: "#64748B",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#FEE2E2";
                    e.currentTarget.style.borderColor = "#FCA5A5";
                    e.currentTarget.style.color = "#DC2626";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.borderColor = "#E2E8F0";
                    e.currentTarget.style.color = "#64748B";
                  }}
                >
                  Clear Cart
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
